import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { useSelector } from 'react-redux';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { keys as incidentObjectiveKeys } from './incidentObjectiveHooks';
import { ObjectiveBase } from '../../../types/iapBaseTypes';
import { IapScope, ORGANIZATION } from '../ICSConstants';
import parseErrorMessage from 'utils/parseErrorMessage';
import Sentry from '../../../instrument';

export type Objective = ObjectiveBase & {
  id: UUID;
  group_guid: UUID;
  archived: boolean;
  strategies: UUID[];
};

export const keys = {
  base: 'objectives',
  get all() {
    return [keys.base];
  },
  get: function (group_guid: UUID, user_guid: UUID) {
    return [keys.base, group_guid, user_guid];
  },
} as const;

export const useObjectives = (scope?: IapScope) => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.get(group_guid, user_guid),
    queryFn: async () => {
      const resp = await axios.get<Objective[]>(
        `${config.apiGateway.ics_objectives}/${group_guid}`
      );
      return resp.data;
    },
    enabled: scope === ORGANIZATION,
  });
};

export type CreateObjectiveReqData = ObjectiveBase & {
  incident_id?: UUID;
};

export const useCreateObjective = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateObjectiveReqData) => {
      return await axios.post(`${config.apiGateway.ics_objectives}`, {
        ...data,
        group_guid,
        user_guid,
      });
    },
    onSuccess: (_, reqObj) => {
      queryClient.invalidateQueries({
        queryKey: keys.get(group_guid, user_guid),
      });
      if (reqObj.incident_id) {
        queryClient.invalidateQueries({
          queryKey: incidentObjectiveKeys.all,
        });
      }
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
      Sentry.captureException(error);
    },
  });
};

export type EditObjectiveReqData = ObjectiveBase & {
  id: UUID;
  strategies?: UUID[];
  archived: boolean;
};

export const useEditObjective = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditObjectiveReqData) => {
      const { id, ...updateData } = data;
      return await axios.put(`${config.apiGateway.ics_objectives}/${id}`, {
        ...updateData,
        group_guid,
        user_guid,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.get(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(error.message);
      Sentry.captureException(error);
    },
  });
};

// edit objective?
